import NumberFlow from "@number-flow/react";
import { FC, useEffect } from "react";
import { twMerge } from "tailwind-merge";

import { AICard } from "@/app/dashboard/alerts";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { usePathname } from "@/lib/i18n";
import { textColorBasedOnNumber } from "@/lib/utils";
import * as m from "@/paraglide/messages";
import { getDashboardHoldings } from "@/services/controllers/dashboard";
import { useAppSelector } from "@/services/hooks/redux.hooks";

const Placeholder = () => {
  return (
    <div className="flex lg:flex-row flex-col">
      <div className="gap-4 flex flex-col w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 items-center">
          <div className="flex flex-col gap-2">
            <Skeleton className="h-[20px] w-28" />
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-4">
                <Skeleton className="h-[36px] w-48" />
                <Skeleton className="h-[36px] w-20" />
              </div>
              <div className="flex fle-col gap-1 mt-1">
                <Skeleton className="h-[20px] w-[80px]" />
                <Skeleton className="h-[20px] w-[62px]" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ScreenHeader: FC = () => {
  const pathname = usePathname();
  const { selectedExchange, fetched, loadingKeys } = useAppSelector(
    (state) => state.exchangeSwitcher,
  );
  const { focusedPortfolio, loaded } = useAppSelector(
    (state) => state.dashboard,
  );

  useEffect(() => {
    const controller = new AbortController();
    if (
      !loadingKeys &&
      focusedPortfolio?.id == null &&
      !pathname.includes("dashboard")
    ) {
      getDashboardHoldings({
        signal: controller.signal,
        params: {
          exchange_key_id: selectedExchange?.exchange_key_id,
        },
      });
    }
    return () => {
      controller.abort();
    };
  }, [
    focusedPortfolio?.id,
    loadingKeys,
    pathname,
    selectedExchange?.exchange_key_id,
  ]);

  if (!loaded && !fetched) {
    return <Placeholder />;
  }

  return (
    <div
      className="flex lg:flex-row flex-col justify-between gap-8"
      id="tour1-step1"
    >
      <div className="flex flex-col gap-8 2xl:min-w-[50%] 2xl:w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 items-center">
          <div className="flex  flex-col">
            <p className="text-sm text-muted-foreground">
              {m.portfolio_value()}{" "}
              <strong>
                {selectedExchange?.name === "Overall portfolio"
                  ? "Global"
                  : selectedExchange?.name}
              </strong>
            </p>
            <div className="flex flex-col gap-1 ">
              <div className="flex items-center gap-3">
                <p className="text-3xl lg:text-5xl font-extrabold tracking-tight min-w-fit">
                  <NumberFlow
                    value={
                      focusedPortfolio?.evaluation ??
                      selectedExchange?.last_5m_evaluation ??
                      0
                    }
                    format={{
                      style: "currency",
                      currency: "USD",
                      currencyDisplay: "narrowSymbol",
                    }}
                  />
                </p>
              </div>
              <div className="flex fle-col gap-1">
                <p
                  className={twMerge(
                    textColorBasedOnNumber(
                      focusedPortfolio?.portfolio_pnl.value ??
                        selectedExchange?.["24h_change_value"] ??
                        0,
                    ),
                    "font-bold min-w-fit",
                  )}
                >
                  <NumberFlow
                    value={
                      focusedPortfolio?.portfolio_pnl.value ??
                      selectedExchange?.["24h_change_value"] ??
                      0
                    }
                    format={{
                      style: "currency",
                      currency: "USD",
                      currencyDisplay: "narrowSymbol",
                      signDisplay: "always",
                    }}
                  />
                </p>
                <Badge
                  variant={
                    (focusedPortfolio?.portfolio_pnl.percent ??
                      selectedExchange?.["24h_change_value"] ??
                      0) > 0
                      ? "success"
                      : (focusedPortfolio?.portfolio_pnl.percent ??
                          selectedExchange?.["24h_change_value"]) === 0
                      ? "secondary"
                      : "destructive"
                  }
                >
                  <NumberFlow
                    value={
                      (focusedPortfolio?.portfolio_pnl.percent ??
                        selectedExchange?.["24h_change_percentage"] ??
                        0) / 100
                    }
                    format={{
                      style: "percent",
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    }}
                  />
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex w-full flex-col gap-2 lg:w-1/2 2xl:w-full">
        <div className="grid gap-2">
          {pathname === "/dashboard" ? (
            <div className="relative">
              <AICard />
            </div>
          ) : (
            // pathname === "/smart-allocation" ? (
            //   <SAAICard />
            // ) :
            <></>
          )}
        </div>
      </div>
    </div>
  );
};
